import { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Check, AlertTriangle } from 'lucide-react';
import { ProducerFormData } from '../types';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { validateSlug, normalizeSlug } from '@/lib/slug';

interface BasicInfoSectionProps {
  formData: ProducerFormData;
  errors: Partial<Record<keyof ProducerFormData, string>>;
  updateField: <K extends keyof ProducerFormData>(key: K, value: ProducerFormData[K]) => void;
  showLongDescription: boolean;
  onToggleLongDescription: (show: boolean) => void;
  producerId?: string;
  /** Current slug from DB — used for change detection */
  currentSlug?: string | null;
  /** Slug validation error from save (e.g. uniqueness) */
  slugError?: string | null;
  onSlugErrorClear?: () => void;
}

export function BasicInfoSection({
  formData,
  errors,
  updateField,
  showLongDescription,
  onToggleLongDescription,
  producerId,
  currentSlug,
  slugError,
  onSlugErrorClear,
}: BasicInfoSectionProps) {
  const shortDescriptionCount = formData.shortDescription.length;

  // Local slug validation
  const [localSlugError, setLocalSlugError] = useState<string | null>(null);

  const slugValue = formData.slug ?? '';

  const handleSlugChange = useCallback((raw: string) => {
    const normalized = normalizeSlug(raw);
    updateField('slug', normalized);
    setLocalSlugError(null);
    onSlugErrorClear?.();
  }, [updateField, onSlugErrorClear]);

  const handleSlugBlur = useCallback(() => {
    if (slugValue) {
      const err = validateSlug(slugValue);
      setLocalSlugError(err);
    } else {
      setLocalSlugError(null);
    }
  }, [slugValue]);

  const displaySlugError = slugError || localSlugError || (errors as any).slug;
  const slugChanged = currentSlug != null && slugValue !== currentSlug && slugValue !== '';

  const publicDomain = 'farmly.se';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-foreground">Namn och beskrivning</h3>
      </div>

      <div className="space-y-4 max-w-2xl">
        {/* Name field */}
        <div>
          <Label htmlFor="name">Namn på verksamheten *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="T.ex. Björkeby Gård"
            className={cn(errors.name && 'border-destructive')}
          />
          {errors.name && (
            <p className="text-xs text-destructive mt-1">{errors.name}</p>
          )}
        </div>

        {/* Slug field */}
        <div>
          <Label htmlFor="slug">Sidans adress</Label>
          <div className="flex items-center gap-0 mt-1">
            <span className="inline-flex items-center px-3 h-10 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground whitespace-nowrap">
              {publicDomain}/producer/
            </span>
            <Input
              id="slug"
              value={slugValue}
              onChange={(e) => handleSlugChange(e.target.value)}
              onBlur={handleSlugBlur}
              placeholder="din-gard"
              className={cn(
                'rounded-l-none',
                displaySlugError && 'border-destructive'
              )}
            />
          </div>
          {displaySlugError && (
            <p className="text-xs text-destructive mt-1">{displaySlugError}</p>
          )}
          {slugChanged && !displaySlugError && (
            <div className="flex items-start gap-1.5 mt-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                Om du ändrar sidans adress kan gamla länkar sluta fungera.
              </p>
            </div>
          )}
          {!displaySlugError && !slugChanged && slugValue && (
            <p className="text-xs text-muted-foreground mt-1">
              {publicDomain}/producer/{slugValue}
            </p>
          )}
        </div>

        {/* Short description */}
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="shortDescription">Kort beskrivning (valfritt)</Label>
            <span
              className={cn(
                'text-xs',
                shortDescriptionCount > 500 ? 'text-destructive' : 'text-muted-foreground'
              )}
            >
              {shortDescriptionCount}/500
            </span>
          </div>
          <Textarea
            id="shortDescription"
            value={formData.shortDescription}
            onChange={(e) => updateField('shortDescription', e.target.value)}
            placeholder="En kort teaser om vad gården erbjuder."
            rows={2}
            className={cn(errors.shortDescription && 'border-destructive')}
          />
          {errors.shortDescription && (
            <p className="text-xs text-destructive mt-1">{errors.shortDescription}</p>
          )}
        </div>

        <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
          <div>
            <h4 className="text-sm font-medium text-foreground">Längre beskrivning (valfritt)</h4>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Ge besökare en djupare förståelse för platsen.
              <br />
              En längre beskrivning hjälper rätt gäster att känna igen sig innan de kommer.
              <br />
              Visas på din publika sida.
            </p>
          </div>

          {!showLongDescription ? (
            formData.longDescription ? (
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-green-600" />
                  Längre beskrivning tillagd
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onToggleLongDescription(true)}
                >
                  Redigera
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onToggleLongDescription(true)}
              >
                Lägg till längre beskrivning
              </Button>
            )
          ) : (
            <div>
              <Label htmlFor="longDescription" className="sr-only">Längre beskrivning</Label>
              {producerId ? (
                <RichTextEditor
                  value={formData.longDescription}
                  onChange={(html) => updateField('longDescription', html)}
                  producerId={producerId}
                  placeholder="Berätta mer om gårdens historia, produktion eller filosofi. Du kan lägga till länkar och bilder."
                  expandable
                  expandedTitle="Längre beskrivning"
                  expandedDescription="Berätta om gårdens historia, produktion eller filosofi. Texten visas på er publika sida."
                  expandedSurface="producer_editor"
                  expandedField="long_description"
                />
              ) : (
                <Textarea
                  id="longDescription"
                  value={formData.longDescription}
                  onChange={(e) => updateField('longDescription', e.target.value)}
                  placeholder="Berätta mer om gårdens historia, produktion eller filosofi."
                  rows={5}
                />
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {producerId ? 'Du kan formatera texten och lägga till länkar och bilder.' : 'Spara först för att kunna lägga till bilder och länkar.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
